import './style.css';
import { World } from './engine/World';
import { mountHud } from './layout/hud';
import { Simulator } from './simulator/Simulator';
import { WorldSocket } from './net/wsClient';
import { BILI, playerSrc, WORLD_INIT, DEBUG, syncConfigFromServer, httpBaseFromWs } from './config';

async function boot() {
  // 左侧 B 站视频 iframe(每期更新 BVID,配置项)。BVID 未就绪 → 世界观海报兜底,
  // 决不让 B 站错误页上镜。
  const iframe = document.getElementById('bili-iframe') as HTMLIFrameElement;
  const cap = document.getElementById('video-caption');
  if (BILI.activeBvid) {
    iframe.src = playerSrc(BILI.activeBvid);
    if (cap) cap.textContent = `先知的神庙 · ${BILI.activeBvid}`;
  } else {
    iframe.style.display = 'none';
    const poster = document.createElement('div');
    poster.className = 'video-poster';
    poster.innerHTML = `
      <div class="vp-title">灵墟</div>
      <div class="vp-sub">弹幕造世 · 先知的神庙</div>
      <div class="vp-hint">第一期神谕视频筹备中 · 弹幕即神谕</div>`;
    iframe.parentElement!.appendChild(poster);
    if (cap) cap.textContent = '先知的神庙 · 第一期筹备中';
  }

  const worldHost = document.getElementById('world-canvas-host')!;
  const flavorHost = document.getElementById('flavor-host')!;
  const worldPane = document.getElementById('world-pane')!;
  const simHost = document.getElementById('sim-host')!;

  // 并行:拉服务端权威配置(EPOCHS 门槛/名称),与画布初始化重叠,不额外拖慢启动。
  const cfgSync = syncConfigFromServer(httpBaseFromWs());

  const world = new World(worldHost, flavorHost);
  let webglOk = true;
  try {
    await world.init();
  } catch (e) {
    webglOk = false;
    console.warn('[灵墟] WebGL 初始化失败,降级为文字世界', e);
    showWorldFallback(worldPane);
  }

  await cfgSync;   // 拉到就用权威门槛,拉不到用内置兜底(最多等 3.5s)

  // 初始态:混沌纪 —— "世界一片灰暗,云海中只有一粒火种明灭"(EPOCHS[0] 原文)。
  world.state.setOnline(WORLD_INIT.onlineGods);
  world.state.setPopulation(0);

  mountHud(world, world.state);

  // ── 连接世界指示灯(在线/重连中)──
  const conn = document.createElement('span');
  conn.className = 'conn-dot';
  conn.title = '连接世界中…';
  document.querySelector('.tb-right')!.prepend(conn);

  let sim: Simulator | undefined;
  if (webglOk) {
    // 空心跳 → 世界自呼吸:长时间无神谕时偶尔来一颗流星,世界"一直在动"
    let lastEvent = performance.now();
    const hud = world.onEventPlayed;
    world.onEventPlayed = (ev) => { lastEvent = performance.now(); hud?.(ev); };
    window.setInterval(() => {
      if (performance.now() - lastEvent > 14000 && Math.random() < 0.5) {
        const r = Math.random();
        const type = r < 0.6 ? 'meteor' : r < 0.9 ? 'wind' : 'whale';
        world.playEvent({ type, intensity: 0.5, duration_sec: type === 'meteor' ? 3 : 12, flavor_text: '', source: { kind: 'system' } });
        lastEvent = performance.now();
      }
    }, 6000);

    // 神谕模拟器:仅 ?debug=sky1 时挂载面板/快捷键——普通用户能影响世界的
    // 途径只有 B 站弹幕/投币/评论,本地不留任何触发入口。
    sim = new Simulator(world, worldPane, simHost, DEBUG);

    // 首访引导:纯教学提示,不代发任何神谕(点击仅关闭)。
    if (!localStorage.getItem('lx-onboarded')) {
      window.setTimeout(() => {
        if (localStorage.getItem('lx-onboarded')) return;
        const cta = document.createElement('button');
        cta.className = 'onboard-cta';
        cta.innerHTML = `去 B 站视频发弹幕「我要下雨」,这个世界会真的下雨<small>发弹幕=许愿 · 投币=香火 · 写诗=刻碑</small>`;
        cta.onclick = () => { localStorage.setItem('lx-onboarded', '1'); cta.remove(); };
        worldPane.appendChild(cta);
        window.setTimeout(() => cta.remove(), 30000);
      }, 4000);
    }
    if (DEBUG) {
      await sim.loadFromUrl('/timelines/sample.json', '示例·2分钟无人值守');
      await sim.loadFromUrl('/timelines/ep1.json', '第一期视频·完整分镜');
    }
    // 底栏「按`呼出模拟器」提示:非调试模式下移除
    if (!DEBUG) document.querySelector('#bottombar .hint')?.remove();
  }

  // 世界同步:连服务端 —— 在线则由服务端权威驱动;连不上静默降级为本地。
  // 无 WebGL 时仍连接:HUD 数字照常更新(applyServerState 会跳过画布渲染)。
  const ws = new WorldSocket(world);
  ws.onStatus = (ok) => {
    conn.classList.toggle('online', ok);
    conn.title = ok ? '已连接世界' : '与世界断开,重连中…';
  };
  ws.onFull = setCapacityFull;   // 服务端满员 → 优雅排队浮层(有空位自动进入)
  ws.connect();

  (window as unknown as { coinGod: unknown }).coinGod = { world, sim, ws };
}

/** 服务端满员时的优雅排队浮层(有空位会自动连上,onFull(false) 时移除)。 */
function setCapacityFull(full: boolean) {
  const id = 'capacity-overlay';
  const existing = document.getElementById(id);
  if (!full) { existing?.remove(); return; }
  if (existing) return;
  const el = document.createElement('div');
  el.id = id;
  el.className = 'capacity-overlay';
  el.innerHTML = `
    <div class="cap-box">
      <div class="cap-title">灵墟 · 香客盈门</div>
      <div class="cap-sub">此刻涌入的神明太多,正在为你排队……</div>
      <div class="cap-hint">无需刷新,有空位会自动带你进入这个世界</div>
      <div class="cap-spinner"></div>
    </div>`;
  document.body.appendChild(el);
}

/** WebGL 不可用时的文字兜底,避免白屏(P1-4)。 */
function showWorldFallback(worldPane: HTMLElement) {
  const el = document.createElement('div');
  el.className = 'world-fallback';
  el.innerHTML = `
    <div class="wf-title">灵墟</div>
    <div class="wf-sub">你的设备暂不支持 3D 渲染</div>
    <div class="wf-hint">上方数据仍在实时更新。<br>用支持 WebGL 的现代浏览器可观看完整的活世界。</div>`;
  worldPane.appendChild(el);
}

boot().catch((e) => {
  console.error('灵墟启动失败', e);
  const host = document.getElementById('world-canvas-host');
  if (host) host.innerHTML = `<pre style="color:#f88;padding:20px;white-space:pre-wrap">启动失败:${e?.message ?? e}</pre>`;
});
